import { CalendarClock, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  useCreateNativeBooking,
  useSchedulingAvailability,
  useSchedulingResources,
  useSchedulingServices,
} from '../../hooks/use-scheduling';
import { useToastStore } from '../../stores/toast-store';

import type { SchedulingSlotWire } from '../../lib/api-client';
import type { JSX } from 'react';

const DAY_MS = 24 * 60 * 60_000;

/** Rótulo curto de dia ("Qua 02/07") no fuso local. */
function dayLabel(ms: number): string {
  const d = new Date(ms);
  const wd = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][d.getDay()] ?? '';
  return `${wd} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(iso),
  );
}

/**
 * SchedulingAvailabilityPanel — Frente B Bloco II (agenda NATIVA, gated `scheduling-native`).
 *
 * Visão de DISPONIBILIDADE recurso×tempo + slot-picker do operador: escolhe o serviço,
 * navega o dia, vê os horários LIVRES (cada chip mostra o horário + o recurso que o atende)
 * e clica para AGENDAR sob a guarda de slot livre (`POST /v1/scheduling/book`). 409 (slot
 * tomado entre o fetch e o book) → reconduz com toast honesto + refetch.
 */
export function SchedulingAvailabilityPanel(): JSX.Element {
  const resourcesQ = useSchedulingResources();
  const servicesQ = useSchedulingServices();
  const [serviceId, setServiceId] = useState('');
  const [dayOffset, setDayOffset] = useState(0);
  const book = useCreateNativeBooking();

  const resourceName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of resourcesQ.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [resourcesQ.data]);

  // Efetiva o serviço default assim que o catálogo carrega.
  const services = servicesQ.data?.data ?? [];
  const effectiveServiceId = serviceId !== '' ? serviceId : (services[0]?.id ?? '');

  const dayStartMs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime() + dayOffset * DAY_MS;
  }, [dayOffset]);
  const from = new Date(Math.max(dayStartMs, Date.now())).toISOString();
  const to = new Date(dayStartMs + DAY_MS).toISOString();

  const availabilityQ = useSchedulingAvailability(
    { serviceOfferingId: effectiveServiceId, from, to, limit: 60 },
    effectiveServiceId !== '',
  );
  const slots = availabilityQ.data?.data ?? [];

  const handleBook = (slot: SchedulingSlotWire): void => {
    const svc = services.find((s) => s.id === effectiveServiceId);
    book.mutate(
      { serviceOfferingId: effectiveServiceId, startsAt: slot.startsAt },
      {
        onSuccess: () => {
          useToastStore.getState().push({
            variant: 'success',
            title: 'Agenda',
            description: `Agendado: ${svc?.name ?? 'serviço'} em ${dayLabel(dayStartMs)} ${timeLabel(slot.startsAt)}.`,
            durationMs: 3500,
          });
        },
        onError: (err) => {
          const taken = err instanceof Error && /slot_unavailable|409|conflict/i.test(err.message);
          useToastStore.getState().push({
            variant: taken ? 'info' : 'error',
            title: taken ? 'Horário acabou de ser tomado' : 'Falha ao agendar',
            description: taken
              ? 'Esse horário foi ocupado. Atualizei a lista — escolha outro.'
              : err instanceof Error
                ? err.message
                : 'Erro desconhecido',
            durationMs: 5000,
          });
          void availabilityQ.refetch();
        },
      },
    );
  };

  if (servicesQ.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">Carregando agenda nativa…</div>;
  }
  if (services.length === 0) {
    return (
      <div className="p-4 text-sm text-text-secondary">
        Nenhum serviço de agenda nativa cadastrado neste tenant.
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border p-4"
      style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
      aria-label="Disponibilidade da agenda nativa"
    >
      <div className="mb-3 flex items-center gap-2">
        <CalendarClock size={16} aria-hidden style={{ color: 'var(--fg-3)' }} />
        <h2 className="text-sm font-semibold">Disponibilidade · agenda nativa</h2>
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="mb-1 block text-xs font-medium" style={{ color: 'var(--fg-3)' }}>
            Serviço
          </span>
          <select
            className="rounded border px-2 py-1 text-sm"
            style={{ background: 'var(--bg-surface-2)', borderColor: 'var(--border-default)' }}
            value={effectiveServiceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.defaultDurationMinutes}min)
              </option>
            ))}
          </select>
        </label>

        {/* Tempo: tira de dias (hoje + próximos) */}
        <div className="flex flex-wrap gap-1" role="group" aria-label="Dia">
          {[0, 1, 2, 3, 4, 5, 6].map((off) => (
            <button
              key={off}
              type="button"
              className={`rounded border px-2 py-1 text-xs ${off === dayOffset ? 'font-semibold' : ''}`}
              style={{
                borderColor: off === dayOffset ? 'var(--accent-fg)' : 'var(--border-default)',
                background: off === dayOffset ? 'var(--accent-bg)' : 'var(--bg-surface-2)',
              }}
              onClick={() => setDayOffset(off)}
            >
              {dayLabel(new Date().setHours(0, 0, 0, 0) + off * DAY_MS)}
            </button>
          ))}
        </div>
      </div>

      {/* Recursos atendendo este serviço (eixo recurso) */}
      {(resourcesQ.data?.data.length ?? 0) > 0 && (
        <div className="mb-2 text-xs" style={{ color: 'var(--fg-3)' }}>
          Recursos: {resourcesQ.data?.data.map((r) => r.name).join(' · ')}
        </div>
      )}

      {availabilityQ.isLoading && (
        <div className="flex items-center gap-2 p-3 text-sm text-text-secondary">
          <Loader2 size={14} className="animate-spin" aria-hidden /> Calculando horários livres…
        </div>
      )}
      {!availabilityQ.isLoading && slots.length === 0 && (
        <div className="p-3 text-sm text-text-secondary">
          Sem horários livres para {dayLabel(dayStartMs)}. Tente outro dia.
        </div>
      )}

      {slots.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="availability-slots">
          {slots.map((slot) => {
            const resLabel = slot.resourceIds.map((id) => resourceName.get(id) ?? id).join(', ');
            return (
              <button
                key={slot.startsAt}
                type="button"
                className="rounded border px-3 py-2 text-sm"
                style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface-2)' }}
                title={`Agendar ${timeLabel(slot.startsAt)} — ${resLabel}`}
                onClick={() => handleBook(slot)}
                disabled={book.isPending}
              >
                <span className="font-mono">{timeLabel(slot.startsAt)}</span>
                <span className="ml-2 text-xs" style={{ color: 'var(--fg-3)' }}>
                  {resLabel}
                  {slot.remainingCapacity > 1 ? ` · ${slot.remainingCapacity} vagas` : ''}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
